import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { FiAlertCircle, FiX, FiCheckCircle } from "react-icons/fi";

import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAuthStore } from "@/store/authStore";
import { resendVerificationEmail } from "@/services/auth.service";
import { supabase } from "@/config/supabase";

const CustomerLayout: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  // Tracks whether we've confirmed the user is verified via the live
  // Supabase session, even if the store hasn't caught up yet.
  const [confirmedViaSession, setConfirmedViaSession] = useState(false);

  // On mount (and when the store user changes), double-check the live
  // Supabase auth session so the banner disappears immediately after
  // verification — even if the store object is briefly stale.
  useEffect(() => {
    if (user?.email_confirmed_at) {
      setConfirmedViaSession(true);
      return;
    }

    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const confirmed = data.session?.user?.email_confirmed_at;
      if (confirmed) {
        setConfirmedViaSession(true);
        // Patch the store user so downstream components also see the update.
        if (user && !user.email_confirmed_at) {
          setUser({ ...user, email_confirmed_at: confirmed });
        }
      }
    });

    // Also listen for live auth changes (e.g. user verified in another tab).
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.email_confirmed_at) {
        setConfirmedViaSession(true);
        if (user && !user.email_confirmed_at) {
          setUser({ ...user, email_confirmed_at: session.user.email_confirmed_at });
        }
      }
    });

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, [user, setUser]);

  const isUnverified = user && !user.email_confirmed_at && !confirmedViaSession;
  const showBanner = isUnverified && !dismissed;

  const handleResend = async () => {
    if (!user?.email) return;
    setResending(true);
    setResendError(null);
    setResendSuccess(false);
    try {
      await resendVerificationEmail(user.email);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 6000);
    } catch {
      setResendError("Failed to resend. Please try again.");
      setTimeout(() => setResendError(null), 5000);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="section-container py-16">
      {showBanner && (
        <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                Please verify your email address
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">
                We sent a confirmation link to <span className="font-medium">{user?.email}</span>.
                Check your inbox (and spam folder) and click the link to unlock all features.
              </p>
              {resendSuccess && (
                <p className="flex items-center gap-1 text-sm text-green-700 dark:text-green-400 mt-2">
                  <FiCheckCircle className="w-4 h-4" /> Verification email sent!
                </p>
              )}
              {resendError && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">{resendError}</p>
              )}
              <button
                onClick={handleResend}
                disabled={resending}
                className="mt-2 text-sm font-medium text-amber-800 dark:text-amber-200 underline hover:no-underline disabled:opacity-50"
              >
                {resending ? "Sending…" : "Resend verification email"}
              </button>
            </div>
            <button
              onClick={() => setDismissed(true)}
              aria-label="Dismiss"
              className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 flex-shrink-0"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <DashboardLayout variant="customer">
        <Outlet />
      </DashboardLayout>
    </div>
  );
};

export default CustomerLayout;
